import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

// In Font Awesome Pro the weight is the style: the same glyph name is filled at 900, outlined at
// 400 and thinner at 300. Only these three are @font-face-loaded (assets/scss/key-ui/icons/_config
// .scss), so a fourth would need a face before it could be a line here.
const WEIGHTS = {
    solid: 900,
    regular: 400,
    light: 300,
} as const;

// The theme's colour roles, which are what a glyph carrying meaning normally takes.
const ROLES = ['primary', 'secondary', 'success', 'info', 'warning', 'danger'] as const;

/**
 * `none` inherits the surrounding text colour, a role name takes the theme's colour for it, and any
 * other value is used as written — which is how a screen with colours of its own passes one in.
 */
export type RadIconColor = 'none' | typeof ROLES[number] | (string & {});

/**
 * The icon vocabulary. RAD owns no icon names — an application declares its own by merging into
 * this interface, and `name` then completes against them. Left empty, `name` is any string.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- empty by design; the application merges its names in
export interface RadIconVocabulary {}

export type RadIconName = keyof RadIconVocabulary | `fa:${string}` | `svg:${string}`;

/** What a vocabulary name draws as: a glyph from the icon font, or a piece of custom artwork. */
export type RadIconArt =
    { kind: 'font', glyph: string, fontWeight?: string } |
    { kind: 'art', markup: string, strokeWidth?: string };

/**
 * `weight` is the component's own `weight` input, passed on so the application can decide what it
 * means for whatever it draws — a font weight for a glyph, a line width for artwork.
 */
export type RadIconResolver = (name: string, weight?: string) => RadIconArt;

// The library's own icons are written `fa:` prefixed, so that a name and a glyph are told apart the
// same way with a resolver registered and without one. Anything else is taken as a glyph, which is
// all RAD can do with a vocabulary it has not been given.
const drawNameAsGlyph: RadIconResolver = (name, weight) => {
    const [prefix, value] = (name || '').split(':');
    return { kind: 'font', glyph: prefix === 'fa' ? value : name, fontWeight: weight };
};

const resolver = signal<RadIconResolver>(drawNameAsGlyph);

/**
 * Teach RAD an application's icon vocabulary. Left unset, a name is taken as a Font Awesome glyph,
 * which is what the library does on its own. Reading a signal inside the resolver is how a theme
 * change reaches the components — the computed that calls it picks the dependency up.
 */
export function setRadIconResolver(resolve: RadIconResolver): void {
    resolver.set(resolve || drawNameAsGlyph);
}

@Component({
    selector: 'rad-icon',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[style.font-size]': "'var(--rad-icon-' + size() + ')'",
        '[style.color]': 'colorStyle()',
    },
    // The weight binding sits on the <i> rather than the host: .icon sets font-weight itself, and a
    // value inherited from the host would lose to it.
    template: `
        @if (artwork(); as art) {
            <!-- an <i> rather than a <span>, so that substituted artwork is the same element a glyph is -->
            <i class="rad-icon__art" aria-hidden="true" [style.--icon-art-stroke]="strokeWidth()" [innerHTML]="art"></i>
        } @else {
            <i class="icon icon-{{ glyphName() }}" aria-hidden="true" [style.font-weight]="fontWeight()"></i>
        }
    `,
    styles: `
        :host {
            display: inline-flex;
        }

        .rad-icon__art {
            display: inline-flex;

            ::ng-deep svg {
                width: 1em;
                height: 1em;
                // artwork is drawn light, so weight comes from a stroke laid over the filled outline
                stroke: currentColor;
                stroke-width: var(--icon-art-stroke, 0);
                stroke-linejoin: round;
            }
        }
    `,
})
export class RadIconComponent {

    private sanitizer = inject(DomSanitizer);

    /**
     * An icon name from the application's vocabulary, which a theme can substitute. For an icon
     * that is not the application's to name, `fa:` names a Font Awesome glyph and `svg:` a
     * registered piece of artwork — both drawn as written, with no substitution.
     */
    readonly name = input<RadIconName>();

    /**
     * The icon size, from the `--rad-icon-*` scale: `inline` rides the text, `md` is a control
     * or list marker, `lg` and `xl` are the feature icon at a card's and a page's scale.
    */
    readonly size = input<'inline' | 'md' | 'lg' | 'xl'>('md'); // update src/app/rad-ui/styles/tokens.scss if you add more sizes

    /**
     * The Font Awesome weight, which is what selects the style — `solid` fills the glyph, `regular`
     * outlines it, `light` outlines it thinner. Leave it out to take the theme's `--icon-weight`.
     */
    readonly weight = input<keyof typeof WEIGHTS>();

    /** The glyph's colour, for when the colour carries meaning. */
    readonly color = input<RadIconColor>('none');

    // the weight input is this call site's own requirement, so it outranks the theme's
    private readonly wantedWeight = computed(() => {
        const weight = this.weight();
        return weight ? String(WEIGHTS[weight]) : undefined;
    });

    protected readonly resolved = computed<RadIconArt>(() => resolver()(this.name(), this.wantedWeight()));

    protected readonly glyphName = computed(() => {
        const icon = this.resolved();
        return icon.kind === 'font' ? icon.glyph : '';
    });

    protected readonly artwork = computed(() => {
        const icon = this.resolved();
        return icon.kind === 'art' ? this.sanitizer.bypassSecurityTrustHtml(icon.markup) : null;
    });

    protected readonly fontWeight = computed(() => {
        const icon = this.resolved();
        return icon.kind === 'font' ? icon.fontWeight || null : null;
    });

    protected readonly strokeWidth = computed(() => {
        const icon = this.resolved();
        return icon.kind === 'art' ? icon.strokeWidth : null;
    });

    protected readonly colorStyle = computed(() => {
        const color = this.color();
        let style: string = color;
        if (color === 'none') {
            style = null;
        } else if ((ROLES as readonly string[]).includes(color)) {
            style = `var(--color-${color}-color)`;
        }
        return style;
    });
}
